import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePublicJson } from '../utils/use-public-json';
import { cleanTournamentName } from '../utils/cleanTournamentName';
import { signupPhase } from './signup-window';
import { exclusiveGroupNames } from './exclusive-note';
import SwissGroupPanel from './swiss-group-panel';

interface Category {
  id: number;
  name: string;
  discipline: string;
  maxPlayers: number | null;
  registrationCount: number;
  waitlistCount: number;
}

interface TournamentDetail {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  registrationEndDate: string | null;
  venue: string;
  organizer: string;
  description: string | null;
  status: string;
  format?: 'CLASSIC' | 'SWISS';
  swissRoundCount?: number | null;
  categories: Category[];
  exclusiveCategoryGroups?: number[][];
}

const PLAY_STATUSES = ['ONGOING', 'FINISHED'];

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const DISCIPLINE_LABELS: Record<string, string> = {
  SINGLES: 'Singiel',
  SINGLES_M: 'Singiel mężczyzn',
  SINGLES_F: 'Singiel kobiet',
  DOUBLES: 'Debel',
  DOUBLES_M: 'Debel mężczyzn',
  DOUBLES_F: 'Debel kobiet',
  MIXED: 'Mikst',
  CUSTOM: 'Własna',
};

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: tournament, loading, error } = usePublicJson<TournamentDetail>(id ? `/api/public/events/${id}` : null);

  if (loading) {
    return <div className="event-detail-loading">Ładowanie...</div>;
  }

  if (error || !tournament) {
    return <div className="event-detail-error">Nie udało się załadować turnieju.</div>;
  }

  const phase = signupPhase(tournament, new Date());
  const swiss = tournament.format === 'SWISS';
  const inPlay = PLAY_STATUSES.includes(tournament.status);
  const soloGroup = swiss && inPlay && tournament.categories.length === 1 ? tournament.categories[0] : null;
  const isArchive =
    tournament.status === 'FINISHED' &&
    (tournament.organizer === 'Migracja archiwum' || !tournament.venue || tournament.venue === '—');

  return (
    <div className="event-detail">
      <Link to="/events" className="event-detail-back">
        ← Powrót do listy
      </Link>
      {isArchive && (
        <div className="event-detail-archive-banner">
          Turniej już rozegrany — to archiwalny zapis. Wyniki dostępne w sekcji{' '}
          <Link to="/tournaments">Wszystkie wyniki</Link>.
        </div>
      )}
      {!isArchive && phase === 'WAITLIST' && (
        <div className="event-detail-closed-banner">
          {tournament.registrationEndDate
            ? `Termin zapisów minął ${formatDate(tournament.registrationEndDate)}, ale możesz jeszcze dopisać się do listy oczekujących — organizator dobierze zawodników, gdy zwolni się miejsce.`
            : 'Zapisy są zamknięte, ale możesz jeszcze dopisać się do listy oczekujących — organizator dobierze zawodników, gdy zwolni się miejsce.'}
        </div>
      )}
      {!isArchive && phase === 'CLOSED' && (
        <div className="event-detail-closed-banner">
          {tournament.status === 'DRAFT'
            ? 'Zapisy na ten turniej nie są jeszcze otwarte.'
            : 'Rozgrywki już ruszyły — zapisy są zamknięte.'}
        </div>
      )}
      <h1 className="event-detail-name">{cleanTournamentName(tournament.name)}</h1>
      <div className={`event-detail-meta${isArchive ? ' event-detail-meta--archive' : ''}`}>
        <div>
          <strong>Termin:</strong>{' '}
          {tournament.startDate === tournament.endDate
            ? formatDate(tournament.startDate)
            : `${formatDate(tournament.startDate)} – ${formatDate(tournament.endDate)}`}
        </div>
        {tournament.venue && tournament.venue !== '—' && (
          <div>
            <strong>Miejsce:</strong> {tournament.venue}
          </div>
        )}
        {tournament.organizer && tournament.organizer !== 'Migracja archiwum' && (
          <div>
            <strong>Organizator:</strong> {tournament.organizer}
          </div>
        )}
        {tournament.registrationEndDate && (
          <div>
            <strong>Koniec zapisów:</strong> {formatDate(tournament.registrationEndDate)}
          </div>
        )}
      </div>
      {tournament.description && (
        <p className="event-detail-description">{tournament.description}</p>
      )}

      {!isArchive && soloGroup !== null && (
        <>
          <div className="event-solo-group-actions">
            {phase !== 'CLOSED' && (
              <Link to={`/events/${tournament.id}/register/${soloGroup.id}`} className="event-category-register">
                {phase === 'OPEN' ? 'Zapisz się' : 'Dopisz się do listy oczekujących'}
              </Link>
            )}
            <Link to={`/events/${tournament.id}/participants`} className="event-category-participants">
              Zobacz uczestników
            </Link>
          </div>
          <SwissGroupPanel categoryId={soloGroup.id} heading="h2" />
        </>
      )}

      {!isArchive && soloGroup === null && (
        <>
          <h2>{swiss ? 'Grupy' : 'Kategorie'}</h2>
          {swiss && (
            <p className="event-swiss-note">
              System szwajcarski{tournament.swissRoundCount ? ` — ${tournament.swissRoundCount} rund` : ''}: kolejne
              rundy kojarzone są po wynikach, nikt nie odpada. Zapisujesz się do jednej grupy.
            </p>
          )}
          {tournament.categories.length === 0 ? (
            <p>{swiss ? 'Brak grup.' : 'Brak kategorii.'}</p>
          ) : (
            <ul className="event-categories">
              {tournament.categories.map((cat) => {
                const exclusiveWith = exclusiveGroupNames(
                  tournament.exclusiveCategoryGroups,
                  cat.id,
                  tournament.categories,
                );

                return (
                <li key={cat.id} className="event-category-card">
                  <h3 className="event-category-name">{cat.name}</h3>
                  <div className="event-category-discipline">
                    {DISCIPLINE_LABELS[cat.discipline] ?? cat.discipline}
                  </div>
                  {exclusiveWith.length > 0 && (
                    <div className="event-category-exclusive">
                      Zapis tylko do jednej z: {exclusiveWith.join(', ')}
                    </div>
                  )}
                  <div className="event-category-count">
                    {cat.maxPlayers != null
                      ? `${cat.registrationCount} / ${cat.maxPlayers} uczestników`
                      : `${cat.registrationCount} uczestników`}
                    {cat.waitlistCount > 0 && (
                      <span className="event-category-waiting">
                        {' '}
                        · {cat.waitlistCount} na liście oczekujących
                      </span>
                    )}
                  </div>
                  <div className="event-category-actions">
                    {phase !== 'CLOSED' && (
                      <Link
                        to={`/events/${tournament.id}/register/${cat.id}`}
                        className="event-category-register"
                      >
                        {phase === 'OPEN' ? 'Zapisz się' : 'Dopisz się do listy oczekujących'}
                      </Link>
                    )}
                    {phase === 'CLOSED' && tournament.status !== 'DRAFT' && (
                      <span className="event-category-closed">Zapisy zamknięte</span>
                    )}
                    <Link
                      to={`/events/${tournament.id}/participants`}
                      className="event-category-participants"
                    >
                      Zobacz uczestników
                    </Link>
                    {swiss && inPlay && (
                      <Link
                        to={`/events/${tournament.id}/swiss/${cat.id}`}
                        className="event-category-participants"
                      >
                        Rundy i tabela
                      </Link>
                    )}
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default EventDetail;
